import {
  Block,
  BlockGroup,
  BlockGroup as BlockGroupType,
  Block as BlockType,
  Condition,
} from '../../types/blueprint'

const getBlockValueFromFormData = (
  blockId: string,
  formData: Record<string, unknown>
): unknown => {
  const formValue = formData[blockId]
  if (formValue && typeof formValue === 'object' && 'value' in formValue) {
    return (formValue as { value: unknown }).value
  }
  if (Array.isArray(formValue)) {
    return formValue.map((item) => {
      if (item && typeof item === 'object' && 'value' in item) {
        return (item as { value: unknown }).value
      }
      return item
    })
  }
  return formValue
}

const compareArrays = (arr1: unknown[], arr2: unknown[]): boolean => {
  return (
    arr1.length === arr2.length && arr1.every((value) => arr2.includes(value))
  )
}

const arrayContainsValue = (arr: unknown[], value: unknown): boolean => {
  return arr.includes(value)
}

const evaluateEquals = (
  blockValue: unknown,
  conditionValue: unknown
): boolean => {
  const isBlockValueArray = Array.isArray(blockValue)
  const isConditionValueArray = Array.isArray(conditionValue)

  if (isBlockValueArray && isConditionValueArray) {
    return compareArrays(blockValue as unknown[], conditionValue as unknown[])
  }

  if (isBlockValueArray && !isConditionValueArray) {
    return compareArrays(blockValue as unknown[], [conditionValue])
  }

  if (!isBlockValueArray && isConditionValueArray) {
    return arrayContainsValue(conditionValue as unknown[], blockValue)
  }

  return blockValue === conditionValue
}

const isEmpty = (value: unknown): boolean => {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (!Array.isArray(value) &&
      typeof value === 'object' &&
      Object.keys(value || {}).length === 0)
  )
}

/**
 * Checks if blockValue contains conditionValue
 * For arrays, implements "contains any" logic (OR) - returns true if ANY of the condition values are in the block value
 */
const contains = (blockValue: unknown, conditionValue: unknown): boolean => {
  if (typeof blockValue === 'string') {
    // String contains check
    return String(blockValue)
      .toLowerCase()
      .includes(String(conditionValue).toLowerCase())
  } else if (Array.isArray(blockValue)) {
    if (Array.isArray(conditionValue)) {
      return conditionValue.some((val) => arrayContainsValue(blockValue, val))
    }
    // Single value check
    return arrayContainsValue(blockValue, conditionValue)
  }
  return false
}

/**
 * Checks if blockValue contains ALL conditionValues
 * For arrays, implements "contains all" logic (AND) - returns true only if ALL of the condition values are in the block value
 */
const containsAll = (blockValue: unknown, conditionValue: unknown): boolean => {
  if (typeof blockValue === 'string') {
    return String(blockValue)
      .toLowerCase()
      .includes(String(conditionValue).toLowerCase())
  } else if (Array.isArray(blockValue)) {
    if (Array.isArray(conditionValue)) {
      return conditionValue.every((val) => arrayContainsValue(blockValue, val))
    }
    // Single value check
    return arrayContainsValue(blockValue, conditionValue)
  }
  return false
}

export const evaluateCondition = (
  condition: Condition,
  watchedFormData: Record<string, unknown>
): boolean => {
  const blockValue = getBlockValueFromFormData(
    condition.blockId,
    watchedFormData
  )

  switch (condition.operator) {
    case 'equals':
      return evaluateEquals(blockValue, condition.value)

    case 'notEquals':
      return !evaluateEquals(blockValue, condition.value)

    case 'exists':
      return !isEmpty(blockValue)

    case 'isEmpty':
      return isEmpty(blockValue)

    case 'contains':
      return contains(blockValue, condition.value)

    case 'notContains':
      return !contains(blockValue, condition.value)

    case 'containsAll':
      return containsAll(blockValue, condition.value)

    default:
      return false
  }
}

const hasConditions = (blockOrGroup: BlockType | BlockGroupType): boolean => {
  return !!blockOrGroup.conditions?.length
}

export const shouldRenderBlockOrGroup = ({
  blockOrGroup,
  formData,
  mode,
  isPublic,
}: {
  blockOrGroup: BlockType | BlockGroupType
  formData: Record<string, unknown>
  mode?: 'capture' | 'display'
  isPublic?: boolean
}): boolean => {
  if (isPublic && (blockOrGroup as BlockType).adminOnlyVisibility) return false

  // If a block
  if (!('blocks' in blockOrGroup)) {
    // If no conditions, show
    if (!hasConditions(blockOrGroup)) return true
  } else {
    // If a group
    const groupHasVisibleBlock = blockOrGroup.blocks.some((block) =>
      shouldRenderBlockOrGroup({
        blockOrGroup: block,
        formData,
        mode,
        isPublic,
      })
    )

    // If any block is visible and no conditions, show
    // i.e. Hide groups that have no visible blocks
    if (groupHasVisibleBlock && !hasConditions(blockOrGroup)) return true
  }

  // If in display mode check the saved value of the hide field
  if (mode === 'display') return !blockOrGroup.hide

  // If form data is still loading into context, don't render
  if (!formData) return false

  // Evaluate each group (OR conditions)
  return blockOrGroup.conditions?.some((conditionsGroup) => {
    if (!conditionsGroup?.length) return true

    // Evaluate each condition in the conditionsGroup (AND conditions)
    return conditionsGroup.every((condition) =>
      evaluateCondition(condition, formData)
    )
  })
}

const isOperatorThatDoesntUseAValue = (
  operator: Condition['operator']
): boolean => {
  return operator === 'exists' || operator === 'isEmpty'
}

export const filterInvalidConditions = (
  values: Block | BlockGroup
): Block | BlockGroup => {
  if (!values.conditions) return values

  const cleanedConditions = values.conditions
    .filter((group) => group && group.length > 0)
    .map((group) =>
      group.filter(
        (condition) =>
          condition &&
          condition.blockId &&
          condition.operator &&
          (isOperatorThatDoesntUseAValue(condition.operator) || condition.value)
      )
    )
    .filter((group) => group.length > 0)

  return {
    ...values,
    conditions: cleanedConditions.length > 0 ? cleanedConditions : undefined,
  }
}
